// Legacy state migration: clears stale localStorage entries that may
// contain pre-validator items shaped like Sasha's bug ("0+0=?" with
// implausible distractors). Run once on app start, idempotent.
//
// What it touches:
//   - pw.session.cache  — old in-flight session items (cleared)
//   - any per-child progress whose engineState contains a session
//     with results referencing a 0+0 prompt (engine state preserved
//     except the bad result is dropped)
//
// What it doesn't touch:
//   - mastery records (kept — even if they came from a buggy item, the
//     attempt count is still real)
//   - student profiles, ages, settings
//
// Versioning: writes pw.migration.zeroBugCleared = "1" once it has run.
// Subsequent calls are no-ops.

const MIGRATION_KEY = "pw.migration.zeroBugCleared";
const SESSION_CACHE_KEYS = [
  "pw.session.cache",
  "pw.session.current",
  "pw.placement.cache",
];

interface ResultRecord {
  prompt?: string;
  correctAnswer?: unknown;
  distractors?: unknown[];
}

interface EngineState {
  sessionHistory?: Array<{ results?: ResultRecord[] }>;
  [k: string]: unknown;
}

interface ChildProgress {
  engineState?: EngineState;
  [k: string]: unknown;
}

function isSashaShapedResult(r: ResultRecord): boolean {
  if (typeof r?.prompt === "string" && /\b0\s*\+\s*0\s*=/.test(r.prompt)) return true;
  const ans = Number(r?.correctAnswer);
  if (Number.isFinite(ans) && Math.abs(ans) <= 5 && Array.isArray(r?.distractors)) {
    for (const d of r.distractors) {
      const dn = Number(d);
      if (Number.isFinite(dn) && Math.abs(dn - ans) > 30) return true;
    }
  }
  return false;
}

function safeRead(key: string): unknown {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function safeWrite(key: string, val: unknown): void {
  if (typeof localStorage === "undefined") return;
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* ignore */ }
}

function safeRemove(key: string): void {
  if (typeof localStorage === "undefined") return;
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

interface MigrationReport {
  ran: boolean;
  cachesCleared: string[];
  childrenScanned: number;
  resultsRemoved: number;
}

export function migrateLegacyZeroBug(): MigrationReport {
  const report: MigrationReport = {
    ran: false,
    cachesCleared: [],
    childrenScanned: 0,
    resultsRemoved: 0,
  };
  if (typeof localStorage === "undefined") return report;

  // Idempotency check — guarded because some browsers (privacy modes,
  // disabled storage) throw on getItem.
  try {
    if (localStorage.getItem(MIGRATION_KEY) === "1") return report;
  } catch {
    return report;
  }

  // 1. Clear session caches that might hold bad in-flight items
  for (const key of SESSION_CACHE_KEYS) {
    if (localStorage.getItem(key) != null) {
      safeRemove(key);
      report.cachesCleared.push(key);
    }
  }

  // 2. Walk per-child progress, scrub Sasha-shaped results from session
  // history (we keep the rest; only the bad results are dropped)
  const registry = safeRead("pencilwise-children") as { children?: Array<{ id: string }> } | null;
  const childIds = registry?.children?.map((c) => c.id) ?? [];
  for (const id of childIds) {
    report.childrenScanned++;
    const key = `pencilwise-child-${id}`;
    const progress = safeRead(key) as ChildProgress | null;
    if (!progress?.engineState?.sessionHistory) continue;
    let removed = 0;
    const history = progress.engineState.sessionHistory.map((session) => {
      if (!Array.isArray(session.results)) return session;
      const clean = session.results.filter((r) => {
        const bad = isSashaShapedResult(r);
        if (bad) removed++;
        return !bad;
      });
      return { ...session, results: clean };
    });
    if (removed > 0) {
      const next: ChildProgress = {
        ...progress,
        engineState: { ...progress.engineState, sessionHistory: history },
      };
      safeWrite(key, next);
      report.resultsRemoved += removed;
    }
  }

  try { localStorage.setItem(MIGRATION_KEY, "1"); } catch { /* ignore */ }
  report.ran = true;
  return report;
}
